import type {
	Parameters,
	ExtensionManifest,
	DefaultExtensionProvider,
} from '@atlaskit/editor-common/extensions';

import type { CustomSearchExtensionProps } from '@atlassian/custom-search-extension';
import {
	ExtensionMode,
	type CustomSitesExtensionProviderProps,
} from '@atlassian/company-hub-common';
import type { LinksMenuExtensionProps } from '@atlassian/links-menu-extension';
import type { SectionExtensionProps } from '@atlassian/section-extension';
import type { spotlightExtensionProviderWrapperProps } from '@atlassian/spotlight-extension';
import { hubElementPlaceholderExtensionProvider } from '@atlassian/hub-element-placeholder';
import { getSectionExtensionContents } from '@atlassian/hub-providers';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import { ConfluenceDependenciesProvider } from './platform-props-providers/ConfluenceDependenciesProvider.tsx';

const cardsExtensionProviderLoader = async (props) => {
	const module = await import(
		/* webpackChunkName: "loadable-cardsExtensionProvider" */ '@atlassian/cards-extension'
	);
	return module.cardsExtensionProvider(props);
};

const carouselExtensionProviderLoader = async (props) => {
	const module = await import(
		/* webpackChunkName: "loadable-carouselExtensionProvider" */ '@atlassian/carousel-extension'
	);
	return module.carouselExtensionProvider(props);
};

const platformSearchExtensionProviderLoader = async (props: CustomSearchExtensionProps) => {
	const module = await import(
		/* webpackChunkName: "loadable-platformSearchExtensionProvider" */ '@atlassian/custom-search-extension'
	);
	return module.searchExtensionProvider(props);
};

const platformSpotlightExtensionProviderLoader = async (
	props: spotlightExtensionProviderWrapperProps,
) => {
	const module = await import(
		/* webpackChunkName: "loadable-platform-spotlight-extension" */ '@atlassian/spotlight-extension'
	);
	return module.spotlightExtensionProvider(props);
};

const platformLinksMenuExtensionProviderLoader = async (props: LinksMenuExtensionProps) => {
	const module = await import(
		/* webpackChunkName: "loadable-platformLinksMenuExtensionProvider" */ '@atlassian/links-menu-extension'
	);
	return module.linksMenuExtensionProvider(props);
};

const platformSectionExtensionProviderLoader = async (props: SectionExtensionProps) => {
	const module = await import(
		/* webpackChunkName: "loadable-platformSectionExtensionProvider" */ '@atlassian/section-extension'
	);
	return module.sectionExtensionProvider(props);
};

export const createPremiumExtensionManifests = async (
	props: CustomSitesExtensionProviderProps,
): Promise<ExtensionManifest<Parameters>[]> => {
	const extensionProviders = listPremiumExtensionProviders(
		props.contentId,
		props.allowCompanyHubExclusiveExtensions,
	);

	const premiumExtensions: ExtensionManifest<Parameters>[] = [];

	for (const provider of extensionProviders) {
		try {
			const instance = await provider(props);
			const manifests = await instance.getExtensions();
			premiumExtensions.push(...manifests);
		} catch (error) {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.MIGRATIONS_TOOLING,
			});
		}
	}

	return premiumExtensions;
};

type ExtensionProvider = (
	props: CustomSitesExtensionProviderProps,
) => Promise<DefaultExtensionProvider<Parameters>>;

function listPremiumExtensionProviders(
	contentId: string,
	allowCompanyHubExclusiveExtensions?: boolean,
) {
	const extensionProviders: ExtensionProvider[] = [];

	extensionProviders.push(async (providerProps) =>
		(
			await platformSpotlightExtensionProviderLoader({
				contentId,
				mode: ExtensionMode.EDITOR,
				propsProvider: ConfluenceDependenciesProvider,
			})
		)(providerProps),
	);

	extensionProviders.push(async (providerProps) =>
		(
			await carouselExtensionProviderLoader({
				contentId,
				propsProvider: ConfluenceDependenciesProvider,
			})
		)(providerProps),
	);

	extensionProviders.push(async (providerProps) =>
		(
			await cardsExtensionProviderLoader({
				contentId,
				mode: ExtensionMode.EDITOR,
				propsProvider: ConfluenceDependenciesProvider,
			})
		)(providerProps),
	);

	if (allowCompanyHubExclusiveExtensions) {
		extensionProviders.push(async (providerProps) =>
			(
				await platformLinksMenuExtensionProviderLoader({
					contentId,
					mode: ExtensionMode.EDITOR,
					propsProvider: ConfluenceDependenciesProvider,
				})
			)(providerProps),
		);
		extensionProviders.push(async (providerProps) =>
			(
				await platformSectionExtensionProviderLoader({
					contentId,
					mode: ExtensionMode.EDITOR,
					propsProvider: ConfluenceDependenciesProvider,
					getSectionExtensionContents,
				})
			)(providerProps),
		);
		extensionProviders.push(hubElementPlaceholderExtensionProvider);

		extensionProviders.push(async (providerProps) =>
			(
				await platformSearchExtensionProviderLoader({
					contentId,
					mode: ExtensionMode.EDITOR,
					propsProvider: ConfluenceDependenciesProvider,
				})
			)(providerProps),
		);
	}

	return extensionProviders;
}
